export type EventStreamVisibility = {
  state: () => string | undefined
  listen: (listener: () => void) => () => void
}

export const eventStreamIsHidden = (visibilityState: string | undefined) => visibilityState === "hidden"

/**
 * How long a stream may stay silent before the client treats it as stalled. A
 * live stream allows two missed 3-second heartbeats plus a second of delivery
 * jitter, on a link that has not stalled recently (see event-stream-link.ts).
 * An idle stream outlasts two of the runtime's 25-second idle heartbeats.
 */
export const EVENT_STREAM_TIMEOUT_MS = { live: 7_000, idle: 60_000 } as const

/**
 * A stall timer that fires at least this late ran after the device slept or the
 * page froze, so the silence it measured says nothing about the route.
 */
export const EVENT_STREAM_SUSPENDED_MS = 1_000

/**
 * A stream that shows no workspace only feeds sidebar summaries, such as a
 * background machine's, so it asks the runtime for slow heartbeats. Events
 * still arrive immediately; only stall detection is slower.
 */
export function eventStreamPace(interest: readonly string[]): keyof typeof EVENT_STREAM_TIMEOUT_MS {
  return interest.some((directory) => directory !== "global") ? "live" : "idle"
}

export function eventStreamHeartbeatDelay(input: {
  visibilityState: string | undefined
  lastEventAt: number
  now: number
  timeoutMs: number
}) {
  if (eventStreamIsHidden(input.visibilityState)) return
  const elapsed = Math.max(0, input.now - input.lastEventAt)
  return Math.max(0, input.timeoutMs - elapsed)
}

export function waitForEventStreamVisibility(source: EventStreamVisibility, signal: AbortSignal) {
  if (!eventStreamIsHidden(source.state())) return Promise.resolve()

  return new Promise<void>((resolve, reject) => {
    let settled = false
    let unsubscribe = () => undefined
    const finish = (error?: DOMException) => {
      if (settled) return
      settled = true
      unsubscribe()
      signal.removeEventListener("abort", onAbort)
      if (error) reject(error)
      else resolve()
    }
    const onVisibility = () => {
      if (!eventStreamIsHidden(source.state())) finish()
    }
    const onAbort = () => finish(new DOMException("Aborted", "AbortError"))
    const stopListening = source.listen(onVisibility)
    unsubscribe = stopListening
    if (settled) stopListening()
    signal.addEventListener("abort", onAbort, { once: true })

    if (signal.aborted) onAbort()
    else onVisibility()
  })
}
